import { Formik } from 'formik';
import type { ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import {
  ScorecardFillBody,
  useScorecardValidation,
} from '@/components/shared/scorecard';
import type { ScorecardType } from '@/types/interview';
import { ScorecardAutoSave } from './AutoSaveScorecard';

interface CollectScoreCardFormProps {
  formRef: React.MutableRefObject<any>;
  formConfig: Array<{ name: string | number; [key: string]: unknown }>;
  scorecard: ScorecardType;
  handleSubmit?: (_values: Record<string, any>) => void;
  initialValues?: Record<string, any>;
  handleAutoSave?: (_values: Record<string, any>) => void;
  status?: string;
  disabled?: boolean;
  ratingBanner?: ReactNode;
  /**
   * Bumped by the host on AI-fill / clear to remount so the rich-text editors
   * re-seed from the new field values (tiptap only reads its value on mount).
   */
  formKey?: number;
}

export const CollectScoreCardForm = ({
  formRef,
  formConfig,
  scorecard,
  handleSubmit = () => {},
  initialValues = {},
  handleAutoSave = () => {},
  status = '',
  disabled = false,
  ratingBanner = null,
  formKey = 0,
}: CollectScoreCardFormProps) => {
  const { errors, scrollToErrorToken, validate } = useScorecardValidation(
    scorecard,
    formConfig
  );

  // Gate the submit with the BE-matching check (required + per-answer
  // validity). On failure the builder shows inline errors + scrolls and we
  // never hit the API; on success we submit the coerced values. There's no
  // Formik `validationSchema` — this is the gate.
  const onSubmit = (rawValues: Record<string, any>) => {
    const { valid, values } = validate(rawValues);
    if (!valid) return;
    handleSubmit(values);
  };

  return (
    <Formik
      key={formKey}
      enableReinitialize
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      <Box w="100%">
        <ScorecardFillBody
          scorecard={scorecard}
          formConfig={formConfig}
          disabled={disabled}
          ratingBanner={ratingBanner}
          errors={errors}
          scrollToErrorToken={scrollToErrorToken}
        />
        {status !== 'submitted' && (
          <ScorecardAutoSave onAutoSave={handleAutoSave} />
        )}
      </Box>
    </Formik>
  );
};
